import React, {
	JSX,
	useCallback,
	useContext,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import classNames from 'classnames';
import { styled } from '@mui/material/styles';
import { Avatar } from '../../message/Avatar';
import { SenderSource, type IChatMessagesOut } from '@enuan/chat__utils';
import { IClientMessage } from '../../../interfaces/IClientMessage';
import { useMessageReadEffect } from '../../../hooks/useMessageReadEffect';
import { useEnuanLocalStorage } from '../../../hooks/useEnuanLocalStorage';
import { useDispatch } from 'react-redux';
import { messagesSlice } from 'src/reduxStore/slices/messages.slice';
import { useSendMessage } from 'src/hooks/core/messages.hooks';
import { useLogError } from '../../ErrorBoundary/useLogError';
import {
	MessageExtra,
	type IMessageExtraProps,
} from './components/MessageExtra';
import {
	MessageFooter,
	type IMessageFooterProps,
} from './components/MessageFooter';
import {
	MessageHeader,
	type IMessageHeaderProps,
} from './components/MessageHeader';
import { ErrorBoundary } from 'src/components/ErrorBoundary/ErrorBoundary';
import { configurationContext } from 'src/contexts/configuration/context';

/**
 * Message component props.
 * @typedef {Object} IMessageProps
 */

export type IMessageProps = Omit<JSX.IntrinsicElements['article'], 'ref'> & {
	direction?: 'in' | 'out';
	message: IClientMessage;
	customHeader?: React.ReactNode;
	headerProps?: Omit<IMessageHeaderProps, 'customHeader' | 'sender'>;
	customFooter?: React.ReactNode;
	footerProps?: Omit<
		IMessageFooterProps,
		| 'customFooter'
		| 'onResend'
		| 'onDebugLongPress'
		| 'timestamp'
		| 'sent'
		| 'read'
		| 'sender'
	>;
	customExtra?: React.ReactNode;
	extraProps?: Omit<IMessageExtraProps, 'customExtra' | 'hasDebug' | 'message'>;
	customAvatar?: React.ReactNode;
	wrapBalloon?: boolean;
	balloonProps?: Omit<JSX.IntrinsicElements['div'], 'children'>;
	getRead?: (event: 'intersect' | 'click') => boolean | undefined;
};

/** Optional Message V2 props without `message`, useful for custom message extensions. */
export type IOptionalMessageProps = Partial<Omit<IMessageProps, 'message'>>;

/**
 * Container component for a single chat message.
 *
 * **Behaviour:**
 * - Uses a CSS grid to lay out header, avatar, balloon (content), footer and extra.
 * - The `direction` ('in' | 'out') controls alignment (left/right) and balloon styling.
 * - Manages "read" state via viewport intersection: when the message enters view, `toggleRead` is called and updates storage and Redux.
 * - Long-press on the "time" component in the footer toggles debug mode (when enabled in storage), showing the message JSON in MessageExtra.
 * - When `wrapBalloon` is true, content (children) is wrapped in a balloon div with left/right styles and classes.
 * - Header, footer and extra are optional and can be overridden with custom nodes (`customHeader`, `customFooter`, `customExtra`).
 * - Avatars respect the chat configuration hide flags before rendering the
 *   default avatar slot.
 *
 * @param {IMessageProps} props - Message props (direction, message, custom slots, wrapBalloon, etc.)
 * @param {React.Ref<HTMLElement>} ref - Ref forwarded to the root `<article>` element
 * @returns {JSX.Element} Article element with grid layout
 */
export const Message = styled(
	React.forwardRef<HTMLElement, IMessageProps>(
		(
			{
				direction: directionProp,
				className,
				message,
				customHeader,
				headerProps,
				customFooter,
				footerProps,
				customExtra,
				extraProps,
				customAvatar,
				wrapBalloon = true,
				balloonProps,
				children,
				onClick: onClickProp,
				getRead,
				...props
			},
			ref,
		) => {
			const dispatch = useDispatch();
			const { chat } = useContext(configurationContext);

			const { get: getStorage } = useEnuanLocalStorage();
			const articleRef = useRef<HTMLDivElement>(null!);
			useImperativeHandle(ref, () => articleRef.current);

			const {
				client_id,
				sender,
				timestamp,
				_meta: { selected, sent, read } = {},
			} = message;

			const direction: 'in' | 'out' =
				directionProp ?? (sender === SenderSource.USER ? 'out' : 'in');

			const [hasDebug, setHasDebug] = useState(false);
			const onDebugLongPress = useCallback(
				() => setHasDebug((prev) => !!getStorage('debug') && !prev),
				[getStorage],
			);

			const sendMessage = useSendMessage();
			const logError = useLogError('Message', { message });
			const onResend = useCallback(() => {
				if (sent !== false || sender === SenderSource.USER) return;
				sendMessage(message as IClientMessage<IChatMessagesOut>).catch(
					(error: unknown) => logError('resend', { error }),
				);
			}, [sent, sender, sendMessage, message, logError]);

			const isRead = read || message.sender === SenderSource.USER;
			const setRead = useMessageReadEffect(articleRef, message, getRead);

			const toggleSelected = useCallback(
				(newSelected: IClientMessage['_meta']['selected'] = !selected) => {
					dispatch(
						messagesSlice.actions.toggleSelected({
							client_id,
							selected: newSelected,
						}),
					);
				},
				[dispatch, client_id, selected],
			);

			const handleMessageClick = useCallback(
				(e: React.MouseEvent<HTMLElement>) => {
					toggleSelected();
					setRead('click');
					onClickProp?.(e);
				},
				[toggleSelected, setRead, onClickProp],
			);

			const toggleError = useCallback(() => {
				dispatch(messagesSlice.actions.toggleError({ client_id, error: true }));
			}, [dispatch, client_id]);

			const layoutDirection: 'left' | 'right' =
				direction === 'out' ? 'right' : 'left';
			const headerPropsResolved = headerProps || {};
			const footerPropsResolved = footerProps || {};
			const extraPropsResolved = extraProps || {};
			const { className: headerClassName, ...headerRest } = headerPropsResolved;
			const { className: footerClassName, ...footerRest } = footerPropsResolved;
			const {
				className: extraClassName,
				position: extraPosition,
				...extraRest
			} = extraPropsResolved;
			const {
				className: balloonClassName,
				onClick: balloonOnClick,
				...balloonWrapperProps
			} = balloonProps || {};
			const onBalloonClick = useCallback(
				(e: React.MouseEvent<HTMLDivElement>) => {
					e.stopPropagation();
					balloonOnClick?.(e);
				},
				[balloonOnClick],
			);
			const isSending = !timestamp && !!sent;
			const isAvatarHidden = shouldHideAvatar(sender, chat);

			return (
				<ErrorBoundary
					FallbackRender={() => null}
					id={`message--${client_id}`}
					meta={message}
					errorHandler={toggleError}
				>
					<article
						className={classNames(
							'message',
							`message--${layoutDirection}`,
							isRead && 'message--read',
							isSending && 'message--sending',
							isAvatarHidden && 'message--avatar-hidden',
							selected && 'message--selected',
							className,
						)}
						onClick={handleMessageClick}
						id={`message--${client_id}`}
						{...props}
						ref={articleRef}
					>
						{customHeader !== null && (
							<MessageHeader
								className={classNames(
									'Message-header',
									'message__header',
									headerClassName,
								)}
								customHeader={customHeader}
								sender={message.sender}
								{...headerRest}
							/>
						)}
						{customAvatar !== null &&
							!isAvatarHidden &&
							(customAvatar ??
								(sender && (
									<Avatar sender={sender} className="Message-avatar" />
								)))}

						{wrapBalloon ? (
							<div
								className={classNames(
									'message__balloon',
									'Message-balloon',
									balloonClassName,
								)}
								onClick={onBalloonClick}
								{...balloonWrapperProps}
							>
								{children}
							</div>
						) : (
							children
						)}
						{customFooter !== null && !wrapBalloon && (
							<MessageFooter
								className={classNames(
									'Message-footer',
									'message__footer',
									footerClassName,
								)}
								customFooter={customFooter}
								onResend={onResend}
								onDebugLongPress={onDebugLongPress}
								timestamp={message.timestamp}
								sent={message._meta?.sent}
								read={message._meta?.read}
								sender={message.sender}
								{...footerRest}
							/>
						)}
						{customExtra !== null && (
							<MessageExtra
								className={classNames(
									'Message-extra',
									'message__extra',
									extraClassName,
								)}
								customExtra={customExtra}
								hasDebug={hasDebug}
								message={message}
								position={extraPosition}
								{...extraRest}
							/>
						)}
					</article>
				</ErrorBoundary>
			);
		},
	),
	{ label: 'Message', target: 'Message-root' },
)({
	padding: '0 20px',
	display: 'grid',
	gridTemplateRows: 'auto 1fr auto auto',
	gap: '0.175em 5px',
	gridTemplateAreas: [
		'"top-left header top-right"',
		'"left main right"',
		'"bottom-left footer bottom-right"',
		'"extra-left extra extra-right"',
	].join(' '),
	gridTemplateColumns: '30px 1fr 15px',

	'& .Message-balloon': {
		gridArea: 'main',
		backgroundColor: 'var(--message__balloon_background)',
		padding: '0.6em 0.9em 0.9em 0.6em',
		maxWidth: '100%',
		boxSizing: 'border-box',
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
	},
	'&.message--right .Message-header': {
		justifyContent: 'flex-end',
	},
	'&.message--left .Message-balloon': {
		borderTopRightRadius: '0.6em',
		borderBottomRightRadius: '0.6em',
		borderBottomLeftRadius: '0.6em',
		borderTopLeftRadius: 0,
		justifySelf: 'flex-start',
		backgroundColor: 'var(--message--left__balloon_background)',
	},
	'&.message--right .Message-balloon': {
		borderTopLeftRadius: '0.6em',
		borderBottomLeftRadius: '0.6em',
		borderTopRightRadius: '0.6em',
		borderBottomRightRadius: 0,
		justifySelf: 'flex-end',
		backgroundColor: 'var(--message--right__balloon_background)',
	},
	'&.message--left .Message-avatar': {
		gridArea: 'top-left',
		alignSelf: 'flex-end',
		marginBottom: '-5px',
	},
	'&.message--right': {
		gridTemplateColumns: '15px 1fr 30px',
	},
	'&.message--avatar-hidden': {
		gridTemplateColumns: '0 1fr 15px',
	},
	'&.message--right.message--avatar-hidden': {
		gridTemplateColumns: '15px 1fr 0',
	},
	'&.message--right .Message-avatar': {
		gridArea: 'bottom-right',
		alignSelf: 'flex-start',
		marginTop: '-5px',
	},
	'&.message--right .Message-sender': {
		display: 'none',
	},
	'&.message--right .message__avatar .shape': {
		transform: 'scale(-1, -1)',
	},
	'&.message--selected .Message-balloon': {
		border: '1px solid var(--message--selected__balloon_border)',
	},
});

/**
 * Resolves whether the avatar grid column should be collapsed for a sender.
 *
 * An avatar is considered hidden when its `*_logo` field is explicitly `false`
 * (the canonical "none" value). The deprecated `hide_*_avatar` boolean flags
 * are normalised to `*_logo: false` by `adaptUsersConfiguration` at the API
 * boundary, so this function only needs to handle the current model.
 *
 * @param sender - Message sender used to choose the matching logo field.
 * @param chat - Chat configuration slice containing the logo fields.
 * @returns `true` when the avatar slot should be removed from the layout.
 */
const shouldHideAvatar = (
	sender: SenderSource | undefined,
	chat:
		| {
				agent_logo?: string | false;
				system_logo?: string | false;
				user_logo?: string | false;
		  }
		| undefined,
) => {
	if (sender === SenderSource.USER) return chat?.user_logo === false;
	if (sender === SenderSource.AGENT) return chat?.agent_logo === false;
	if (sender === SenderSource.SYSTEM) return chat?.system_logo === false;
	return false;
};
